import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { catchError, first, NEVER, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { saveAs } from 'file-saver';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UtilsTableService } from '../../services/utils-table.service';
import { ExcelExportService } from '../../services/excel-export.service';
import { Shipment } from '../../../../pages/overview/models/shipment';
import { Container } from '../../../../pages/overview/models/container';
import { Tab } from '../../models/utils-tabs';
import { AsyncPipe } from '@angular/common';
import { Order } from '../../../../pages/overview/models/order';
import { TablePayload } from '../../models/utils-table';
import { TableType } from '../../../../models/shipment';
import { OrderException } from '../../../../pages/overview/models/order-exception';

@Component({
  selector: 'app-excel-export',
  standalone: true,
  templateUrl: './excel-export.component.html',
  imports: [MatButton, MatIcon, MatProgressSpinner, AsyncPipe],
  styleUrl: './excel-export.component.scss'
})
export class ExcelExportComponent implements OnInit, OnDestroy {
  @Input() selectedItems: Shipment[] | Container[] | Order[] | OrderException[] = [];
  @Input() tab: Tab | null = null;
  downloading = false;
  exportingProgress$: Observable<number> = of(0);

  private subscription = new Subscription();

  constructor(
    private matSnackBar: MatSnackBar,
    private utilsTableService: UtilsTableService,
    private excelExportService: ExcelExportService
  ) {}

  ngOnInit() {
    this.exportingProgress$ = this.excelExportService.getProgressFor(this.tab!.id);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  export() {
    if (!this.downloading) {
      const idSelectorByType: Partial<Record<TableType, (item: any) => string>> = {
        Shipments: ({ shipmentNo }) => shipmentNo,
        Containers: ({ id }) => id,
        Orders: ({ id }) => id,
        OrderExceptions: (e: any) => e.id
      };
      const selectedIds = (this.selectedItems as any[]).map(idSelectorByType[this.tab!.type]!);
      this.downloading = true;
      this.matSnackBar.open(
        'File export is in progress. It may take some time but you can continue using the page in the meantime.',
        'x',
        { panelClass: 'snackbar-green' }
      );
      this.subscription.add(
        this.utilsTableService
          .getPageConfigFor(this.tab!.id)
          .pipe(first(), switchMap(this.handleExportFor(selectedIds)))
          .subscribe()
      );
    }
  }

  private handleExportFor(selectedIds: string[]) {
    return (config: Partial<TablePayload>) =>
      this.excelExportService.exportExcel(config, selectedIds).pipe(
        tap(file => {
          saveAs(file, `${this.tab!.name}.xlsx`);
          this.downloading = false;
        }),
        catchError(error => {
          console.error(error);
          this.matSnackBar.open('Could not download the file. Please try again later', 'x');
          this.downloading = false;

          return NEVER;
        })
      );
  }
}
